import { Grid } from '@mui/material';
import { buildCurrencyLevel } from '@vy/services';
import type { ContractOverview, Plan } from '@vy/types';
import {
  conditionalWithDefault,
  isNotEmptyArray,
  isNotNullOrUndefined,
  validateNotNullOrUndefined,
} from '@vy/utils';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useContractsByCustomerId } from '../../../hooks/requests/use-contracts-by-customer-id';
import { useCreditLedgerOverview } from '../../../hooks/requests/use-credit-ledger-overview';
import { useCustomer } from '../../../hooks/requests/use-customer';
import { usePlanById } from '../../../hooks/requests/use-plan-by-id';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { ContractCard } from './customer-view-page-components/ContractCard/ContractCard';
import { Subsidiaries } from './customer-view-page-components/MockSubsidiaries';
import { calculateEstimatedPlanValue } from '../../../utils/contract/calculateEstimatedPlanValue';

const CustomerOverviewPage: React.FC = () => {
  const { customerId } = useValidatedParams<['customerId']>('customerId');
  const { data } = useContractsByCustomerId(customerId);
  validateNotNullOrUndefined(data);

  const contracts = React.useMemo(() => data.contracts ?? [], [data]);

  const customerResponse = useCustomer(customerId, { suspense: true });
  useCreditLedgerOverview(customerId, { suspense: true });
  validateNotNullOrUndefined(customerResponse.data);
  const { customer, children: customerChildren } = customerResponse.data;

  const [contract] = contracts;

  const { data: planResponse } = usePlanById(contract?.plan?.id, {
    suspense: false,
  });
  const plan = planResponse?.plan;

  const estimatedContractValue = useContractEstimator(contract, plan);

  const currency = buildCurrencyLevel({
    customerCurrency: customer.settings?.currency,
  });

  return (
    <>
      <Helmet>
        <title>Customer Overview</title>
      </Helmet>
      <Grid container rowSpacing={2} className="mx-auto max-w-screen-xl">
        <Grid size={12}></Grid>

        <Grid container spacing={5} size={12}>
          {isNotNullOrUndefined(plan) && isNotNullOrUndefined(contract) && (
            <Grid
              size={{
                xs: 12,
                lg: 6
              }}>
              <ContractCard
                plan={plan}
                contractStartDate={conditionalWithDefault(contract.initialStartDate, contract.startDate, () =>
                  isNotNullOrUndefined(contract.revisedAt),
                )}
                estimatedContractValue={estimatedContractValue}
                currency={currency}
              />
            </Grid>
          )}

          {isNotEmptyArray(customerChildren) && (
            <Grid
              size={{
                xs: 12,
                lg: 6
              }}>
              <Subsidiaries subsidiaries={customerChildren} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const CustomerViewPageWithSuspenseBoundary = withSuspenseBoundary(
  withGlobalSearchParams(CustomerOverviewPage),
);

export { CustomerViewPageWithSuspenseBoundary as CustomerOverviewPage };
export function useContractEstimator(contract: ContractOverview | undefined, plan: Plan | undefined) {
  return useMemo(() => {
    if (!plan) {
      return 0;
    }
    return calculateEstimatedPlanValue(plan);
  }, [contract, plan]);
}
